import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * FilterChips — single-select pill group with optional counts (T-0140).
 *
 * Mirrors `.fchip` in `docs/requirement-library/prototype/selection.css`:
 * 28px pills, ink-200 border; the selected pill is ink-900 with paper-0
 * text. Counts render in the mono face.
 *
 * Each pill is a toggle button (`aria-pressed`) inside a labelled
 * group, so Tab reaches every pill and Enter / Space selects it.
 * Filtering is instant: `onChange` fires on click, there is no apply
 * step.
 */

export interface FilterChipOption<V extends string> {
  value: V;
  label: string;
  count?: number;
}

export interface FilterChipsProps<V extends string> {
  options: ReadonlyArray<FilterChipOption<V>>;
  value: V;
  onChange: (value: V) => void;
  /** Accessible name for the group, for example "Filter by origin". */
  'aria-label': string;
  className?: string;
}

export function FilterChips<V extends string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  className,
}: FilterChipsProps<V>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn('flex flex-wrap items-center gap-1.5', className)}
    >
      {options.map((opt) => {
        const on = opt.value === value;
        return (
          <FilterChip key={opt.value} on={on} onClick={() => onChange(opt.value)} count={opt.count}>
            {opt.label}
          </FilterChip>
        );
      })}
    </div>
  );
}

FilterChips.displayName = 'FilterChips';

/**
 * One `.fchip` pill (T-0186): a toggle on its own, for a row of choices
 * that is not a single-select filter (answer options, decline reasons).
 */
export function FilterChip({
  on = false,
  onClick,
  count,
  children,
  disabled,
}: {
  on?: boolean;
  onClick: () => void;
  count?: number;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-meta font-medium',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-60',
        on
          ? 'border-ink-900 bg-ink-900 text-paper-0'
          : 'border-ink-200 bg-paper-0 text-ink-700 hover:border-ink-300',
      )}
    >
      {children}
      {count != null ? (
        <span className="font-mono font-normal opacity-75">{count.toLocaleString()}</span>
      ) : null}
    </button>
  );
}

export interface SearchFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value' | 'type'
> {
  value: string;
  onChange: (value: string) => void;
  'aria-label': string;
  /** Leading icon, usually a 14px search glyph. */
  icon?: React.ReactNode;
  /** Trailing hint, usually a `<kbd className="kbd">/</kbd>`. */
  hint?: React.ReactNode;
  wrapperClassName?: string;
}

/**
 * Toolbar search input for instant filtering (prototype `.rq-search`).
 * Esc blurs it so the page keyboard shortcuts work again.
 */
export const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>(
  ({ value, onChange, icon, hint, wrapperClassName, className, onKeyDown, ...props }, ref) => (
    <label
      className={cn(
        'flex h-8 w-80 items-center gap-2 rounded-chip border border-ink-200 bg-paper-0 px-2.5 text-ink-500 focus-within:ring-2 focus-within:ring-accent-amber-600',
        wrapperClassName,
      )}
    >
      {icon}
      <input
        ref={ref}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.key === 'Escape' && !e.defaultPrevented) {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        className={cn(
          'min-w-0 flex-1 border-0 bg-transparent text-body-sm text-ink-900 placeholder:text-ink-300 focus:outline-none',
          className,
        )}
        {...props}
      />
      {hint}
    </label>
  ),
);
SearchField.displayName = 'SearchField';

/**
 * A tag that is on or off (`.demo-toggle`, T-0187): "Shown on screen" on a
 * proposed evidence item. Green when on.
 */
export function ToggleTag({
  on,
  onClick,
  icon,
  children,
  disabled,
}: {
  on: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex h-[22px] items-center gap-1 whitespace-nowrap rounded-full border px-2 text-[11px] font-medium',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 disabled:cursor-not-allowed',
        on
          ? 'border-transparent bg-posture-healthy-tint text-posture-healthy'
          : 'border-ink-200 text-ink-500 hover:border-ink-300',
      )}
    >
      {icon}
      {children}
    </button>
  );
}
